import { SurfaceBadge } from '@/components/match/surface-badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { cn } from '@/lib/utils';
import type { MatchSearchResult } from '@tennis-scanner/shared';

// Result list for matches.search. Used by the manual pick form's match
// picker; could also feed a global "spotlight" search later.

interface Props {
  results: MatchSearchResult[];
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (m: MatchSearchResult) => void;
  emptyHint?: string;
}

export function MatchSearchResults({
  results,
  isLoading,
  selectedId,
  onSelect,
  emptyHint = 'Escribí al menos 2 caracteres para buscar.',
}: Props) {
  if (isLoading) {
    return (
      <div className="grid gap-2">
        <Skeleton className="h-12" />
        <Skeleton className="h-12" />
        <Skeleton className="h-12" />
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
        {emptyHint}
      </p>
    );
  }

  return (
    <div className="grid gap-2">
      {results.map((m) => {
        const title = m.p1Name && m.p2Name ? `${m.p1Name} vs ${m.p2Name}` : m.id.slice(0, 8);
        const isSelected = selectedId === m.id;
        return (
          <Card
            key={m.id}
            data-slot="match-result"
            className={cn(
              'cursor-pointer p-3 transition-colors hover:bg-popover',
              isSelected && 'ring-2 ring-primary',
            )}
            onClick={() => onSelect(m)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(m);
              }
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{title}</p>
                <p className="flex items-center gap-2 truncate text-xs text-muted-foreground">
                  {m.tournamentLevel && (
                    <span
                      className={cn(
                        'inline-flex shrink-0 items-center rounded-sm border px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider',
                        levelChipClass(m.tournamentLevel),
                      )}
                    >
                      {shortLevel(m.tournamentLevel)}
                    </span>
                  )}
                  <span className="truncate">
                    {m.tournamentName ?? '—'} · BO{m.bestOf} · {m.status}
                  </span>
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <SurfaceBadge surface={m.surface} />
                <span className="font-mono text-[10px] text-muted-foreground">
                  {formatRelativeTime(m.startsAt)}
                </span>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// Compact tournament-level chip — color reflects the betting tier so the
// operator can scan a busy page at a glance.
function shortLevel(raw: string): string {
  const l = raw.toLowerCase();
  if (l.startsWith('atp')) return 'ATP';
  if (l.startsWith('wta')) return 'WTA';
  if (l.startsWith('challenger')) return 'CH';
  if (l.startsWith('itf men')) return 'ITF M';
  if (l.startsWith('itf women')) return 'ITF W';
  return raw.slice(0, 6).toUpperCase();
}

function levelChipClass(raw: string): string {
  const l = raw.toLowerCase();
  if (l.startsWith('atp')) return 'border-info/40 text-info bg-info/10';
  if (l.startsWith('wta')) return 'border-ai-verdict/40 text-ai-verdict bg-ai-verdict/10';
  if (l.startsWith('challenger')) return 'border-warning/40 text-warning bg-warning/10';
  return 'border-muted-foreground/40 text-muted-foreground bg-muted';
}
